import { Chip } from "@a2z/ui";
import type { ChipTone } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type {
  IntegrationLogDirection,
  IntegrationLogStatus,
} from "@/lib/api/types";

// Declared `Partial` on purpose: the audit log mirrors whatever the providers
// report, so a status/direction the admin has no label for must degrade to the
// raw string instead of pushing `undefined` into `t()` (which crashes on
// `.split`, the bug ThemesPage documents).
const STATUS_TONE: Partial<Record<IntegrationLogStatus, ChipTone>> = {
  success: "success",
  failed: "danger",
  pending: "neutral",
};

const STATUS_LABEL_KEY: Partial<Record<IntegrationLogStatus, string>> = {
  success: keys.integrationLogs.statusSuccess,
  failed: keys.integrationLogs.statusFailed,
  pending: keys.integrationLogs.statusPending,
};

const DIRECTION_TONE: Partial<Record<IntegrationLogDirection, ChipTone>> = {
  outbound: "accent",
  inbound: "info",
};

const DIRECTION_LABEL_KEY: Partial<Record<IntegrationLogDirection, string>> = {
  outbound: keys.integrationLogs.directionOutbound,
  inbound: keys.integrationLogs.directionInbound,
};

type StatusChipProps = {
  status: IntegrationLogStatus;
};

export function IntegrationLogStatusChip({ status }: StatusChipProps) {
  const t = useTranslations();
  const labelKey = STATUS_LABEL_KEY[status];

  return (
    <Chip tone={STATUS_TONE[status] ?? "neutral"} size="sm">
      {labelKey ? t(labelKey) : status}
    </Chip>
  );
}

type DirectionChipProps = {
  direction: IntegrationLogDirection;
};

export function IntegrationLogDirectionChip({ direction }: DirectionChipProps) {
  const t = useTranslations();
  const labelKey = DIRECTION_LABEL_KEY[direction];

  return (
    <Chip tone={DIRECTION_TONE[direction] ?? "neutral"} size="sm">
      {labelKey ? t(labelKey) : direction}
    </Chip>
  );
}
